import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    AutocompleteCloseReason,
    Chip,
    ClickAwayListener,
    IconButton,
    InputAdornment,
    InputBase,
    Paper,
    Popper,
    TextField,
} from '@mui/material';
import color from '@theme/Colors';
import { ReactNode, useState } from 'react';
import { IoMdClose } from 'react-icons/io';

type Props<T> = {
    value: T[];
    options: T[];
    /** Receives the full new value; locked items are always kept. */
    onChange(value: T[]): void;
    getOptionKey(option: T): string | number;
    getOptionLabel(option: T): string;
    /** Options must be sorted by this key so each group renders one header. */
    groupBy?(option: T): string;
    /** Text matched against the search box. Defaults to the option label. */
    getSearchText?(option: T): string;
    /** Locked items show as outlined chips, cannot be removed and are disabled in the list. */
    isOptionLocked?(option: T): boolean;
    label?: ReactNode;
    placeholder?: string;
    searchPlaceholder?: string;
    noOptionsText?: ReactNode;
};

/** Render the option list inside our own Paper instead of a separate floating Popper. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PopperInline = ({ disablePortal, anchorEl, open, ...other }: any) => <div {...other} />;

/** Read-only chip field that opens a dropdown with a search box and a (grouped) multi-select list. */
export default function BaseChipSearchMultiSelect<T>(props: Props<T>) {
    const { value, onChange, getOptionKey, getOptionLabel } = props;
    const isLocked = (option: T) => props.isOptionLocked?.(option) ?? false;
    const getSearchText = props.getSearchText ?? getOptionLabel;

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [inputValue, setInputValue] = useState('');
    const open = Boolean(anchorEl);
    const hasRemovable = value.some((v) => !isLocked(v));

    const handleClose = () => {
        setAnchorEl(null);
        setInputValue('');
    };

    const removeItem = (item: T) => onChange(value.filter((v) => getOptionKey(v) !== getOptionKey(item)));

    return (
        <>
            {/* Display field: only shows selected chips, click to open the dropdown */}
            <TextField
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                label={props.label}
                placeholder={props.placeholder}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                InputProps={{
                    readOnly: true,
                    startAdornment: value.map((item) => {
                        const locked = isLocked(item);
                        return (
                            <Chip
                                key={getOptionKey(item)}
                                label={getOptionLabel(item)}
                                size="small"
                                color={locked ? 'primary' : 'default'}
                                variant={locked ? 'outlined' : 'filled'}
                                onDelete={locked ? undefined : () => removeItem(item)}
                                sx={{
                                    '& .MuiChip-deleteIcon': {
                                        fontSize: '12px !important',
                                        width: '12px !important',
                                        height: '12px !important',
                                        mr: '4px',
                                        ml: '-2px',
                                    },
                                }}
                            />
                        );
                    }),
                    endAdornment: (
                        <InputAdornment position="end">
                            {hasRemovable && (
                                <IconButton
                                    className="chip-select-clear-btn"
                                    size="small"
                                    aria-label="Clear"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation(); // không mở dropdown khi bấm X
                                        onChange(value.filter(isLocked));
                                    }}
                                    sx={{ p: '2px', visibility: open ? 'visible' : 'hidden' }}
                                >
                                    <IoMdClose />
                                </IconButton>
                            )}
                            <ArrowDropDownIcon
                                sx={{
                                    color: color.grey600,
                                    transform: open ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s',
                                }}
                            />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: 40,
                        flexWrap: 'wrap',
                        gap: '6px',
                        py: '4px',
                        cursor: 'pointer',
                    },
                    '& .MuiOutlinedInput-input': {
                        width: 0,
                        minWidth: 30,
                        flexGrow: 1,
                        p: '4.5px 4px',
                        cursor: 'pointer',
                    },
                    '&:hover .chip-select-clear-btn': {
                        visibility: 'visible',
                    },
                }}
            />

            {/* Dropdown with search box on top */}
            <Popper
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300, width: anchorEl?.clientWidth }}
            >
                <ClickAwayListener
                    onClickAway={(e) => {
                        // Clicking the display field again should not close then reopen
                        if (anchorEl?.contains(e.target as Node)) return;
                        handleClose();
                    }}
                >
                    <Paper sx={{ mt: 0.5, borderRadius: 2, overflow: 'hidden' }}>
                        <Autocomplete
                            open
                            multiple
                            disableCloseOnSelect
                            value={value}
                            options={props.options}
                            inputValue={inputValue}
                            onInputChange={(_, text, reason) => {
                                // Keep the search text after selecting an option
                                if (reason !== 'reset') setInputValue(text);
                            }}
                            onClose={(_, reason: AutocompleteCloseReason) => {
                                if (reason === 'escape') handleClose();
                            }}
                            groupBy={props.groupBy}
                            getOptionLabel={getOptionLabel}
                            isOptionEqualToValue={(option, v) => getOptionKey(option) === getOptionKey(v)}
                            getOptionDisabled={isLocked}
                            filterOptions={(opts, state) => {
                                const search = state.inputValue.trim().toLowerCase();
                                if (!search) return opts;
                                return opts.filter((o) => getSearchText(o).toLowerCase().includes(search));
                            }}
                            onChange={(_, newValue) => onChange(newValue)}
                            renderTags={() => null}
                            noOptionsText={props.noOptionsText}
                            PopperComponent={PopperInline}
                            componentsProps={{
                                paper: {
                                    elevation: 0,
                                    sx: {
                                        m: 0,
                                        borderRadius: 0,
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight: 300,
                                        },
                                        '& .MuiAutocomplete-groupLabel': {
                                            color: '#8B917F',
                                            fontSize: 14,
                                            lineHeight: '32px',
                                            fontWeight: 600,
                                        },
                                        '& .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-listbox .MuiAutocomplete-option[aria-selected="true"].Mui-focused':
                                        {
                                            bgcolor: 'action.hover',
                                        },
                                    },
                                },
                            }}
                            renderInput={(params) => (
                                <InputBase
                                    ref={params.InputProps.ref}
                                    inputProps={params.inputProps}
                                    autoFocus
                                    fullWidth
                                    placeholder={props.searchPlaceholder ?? 'Search'}
                                    startAdornment={
                                        <SearchIcon sx={{ fontSize: 18, color: color.grey600, mr: 1 }} />
                                    }
                                    sx={{
                                        px: 2,
                                        py: 1.25,
                                        borderBottom: `1px solid ${color.grey300}`,
                                    }}
                                />
                            )}
                        />
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
